import React, { useState } from 'react';
import { Box, Checkbox, Chip, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faCircle, faCalendar } from '@fortawesome/free-regular-svg-icons';
import { useGetRemindersQuery, useUpsertReminderMutation } from '../../../../api/remindersApi';
import { useSelector } from 'react-redux';
import { selectedUserIdSelector } from '../../../../selectors/userSelectors';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import RemindersModal from './RemindersModal';
import { Loader } from '../../../Common';

const RemindersSection = () => {
  const userId = useSelector(selectedUserIdSelector);
  const [modalStatus, setModalStatus] = useState({
    open: false,
    selectedReminder: null
  });

  const { data: reminders = [], isLoading: areRemindersLoading } = useGetRemindersQuery(userId);

  const [upsertReminder] = useUpsertReminderMutation();

  const openModal = (reminder = null) => {
    setModalStatus({ open: true, selectedReminder: reminder });
  };

  const handleToggleReminder = (reminder) => {
    upsertReminder({ ...reminder, isCompleted: !reminder.isCompleted });
  };

  return (
    <div data-testid='reminders-section'>
      {areRemindersLoading ? (
        <Stack
          sx={{
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(87, 185, 255, 0.2)'
          }}
        >
          <Loader />
        </Stack>
      ) : (
        <>
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: 2,
              mt: 2,
              height: '100%'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 1,
                mb: 2
              }}
            >
              <Stack
                direction='row'
                sx={{
                  alignItems: 'center',
                  width: '100%'
                }}
              >
                <Typography variant='h6'>Todos{reminders.length > 0 ? ` (${reminders.length})` : ''}</Typography>
                <IconButton size='large' sx={{ marginLeft: 'auto' }} onClick={() => openModal()}>
                  <FontAwesomeIcon color='black' icon={faPenToSquare} />
                </IconButton>
              </Stack>
            </Box>
            <Divider />
            {reminders &&
              reminders.map((reminder) => (
                <Stack
                  direction='row'
                  key={reminder.id}
                  onClick={() => {
                    openModal(reminder);
                  }}
                >
                  <Checkbox
                    disableRipple
                    checked={reminder.isCompleted}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleToggleReminder(reminder)}
                    icon={<FontAwesomeIcon icon={faCircle} size='xl' />}
                    checkedIcon={<FontAwesomeIcon icon={faCircleCheck} size='xl' />}
                    sx={{
                      p: 0,
                      ml: 1,
                      mr: 2
                    }}
                  />
                  <Stack
                    direction='column'
                    sx={{
                      gap: 1,
                      my: 3
                    }}
                  >
                    <Typography variant='h6'>{reminder.title}</Typography>
                    <Stack
                      direction='row'
                      sx={{
                        gap: 1
                      }}
                    >
                      <FontAwesomeIcon size='lg' icon={faCalendar} />
                      <Typography variant='body1'>{moment.utc(reminder.scheduledAt).format('MM/DD/YYYY')}</Typography>
                    </Stack>
                    <Typography
                      variant='body1'
                      sx={{
                        display: '-webkit-box',
                        wordWrap: 'break-word',
                        wordBreak: 'break-word',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {reminder.notes}
                    </Typography>
                  </Stack>
                  <Stack
                    direction='column'
                    sx={{
                      ml: 'auto',
                      mr: 2,
                      my: 3
                    }}
                  >
                    {reminder.bookkeeperMentions.map((mention) => (
                      <Chip
                        color='primary'
                        variant='outlined'
                        sx={{
                          my: 0.5,
                          width: 'fit-content',
                          height: 'auto',
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            overflow: 'visible'
                          }
                        }}
                        key={mention.id}
                        label={`${mention.bookkeeper.firstname} ${mention.bookkeeper.lastname}`}
                      />
                    ))}
                  </Stack>
                </Stack>
              ))}
          </Paper>
          <RemindersModal status={modalStatus} setStatus={setModalStatus} />
        </>
      )}
    </div>
  );
};

export default RemindersSection;
